import type { LegalDoc } from '../components/LegalPage';
import type { MineTab } from '../components/MyContentView';

/**
 * Direcciones de la app. Cada sección tiene la suya para que recargar, copiar
 * el enlace o usar Atrás/Adelante del navegador te deje donde estabas.
 *
 * El servidor responde con la app a cualquier ruta que no sea de /api, así
 * que añadir aquí una sección nueva no pide tocar nada más.
 */

export type Screen =
  | 'home'
  | 'create'
  | 'room'
  | 'study'
  | 'leaderboard'
  | 'profile'
  | 'wallet'
  | 'guardian'
  | 'users'
  | 'user'
  | 'quiz'
  | 'legal'
  | 'recordings'
  | 'friends'
  | 'leagues'
  | 'admin'
  | 'mine'
  | 'exam'
  | 'task';

export type HomeTab = 'quizzes' | 'rooms';

export interface Route {
  screen: Screen;
  homeTab?: HomeTab;
  quizId?: string;
  examCode?: string;
  taskCode?: string;
  legalDoc?: LegalDoc;
  userId?: string;
  mineTab?: MineTab;
  leagueId?: string;
  pin?: string;
}

/** Secciones de ruta fija, sin nada variable dentro. */
const FIXED: Partial<Record<Screen, string>> = {
  leaderboard: '/ranking',
  users: '/jugadores',
  leagues: '/ligas',
  friends: '/amigos',
  profile: '/perfil',
  wallet: '/dahus',
  guardian: '/guardian',
  recordings: '/grabaciones',
  admin: '/admin',
};

/** Secciones que solo existen con sesión iniciada. */
export const NEEDS_ACCOUNT: ReadonlySet<Screen> = new Set<Screen>([
  'create',
  'profile',
  'wallet',
  'guardian',
  'friends',
  'recordings',
  'admin',
  'mine',
]);

/**
 * Secciones cuya vista interna cuelga de su dirección (/ligas/<id>,
 * /mis-examenes/<id>…) y la gestiona la propia sección con useSubPath. App
 * no la toca mientras la base coincida.
 */
export const SUBPATH_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'leagues',
  'mine',
  'recordings',
  'admin',
  'friends',
]);

/** ¿`pathname` es `base` o algo que cuelga de ella? */
export function isUnder(pathname: string, base: string): boolean {
  return pathname === base || (base !== '/' && pathname.startsWith(base + '/'));
}

const seg = (s: string) => encodeURIComponent(s);

export function routeToPath(r: Route): string {
  switch (r.screen) {
    case 'home':
      return r.homeTab === 'rooms' ? '/sala-de-espera' : '/';
    case 'quiz':
      return r.quizId ? `/t/${seg(r.quizId)}` : '/';
    case 'exam':
      return r.examCode ? `/e/${seg(r.examCode)}` : '/';
    case 'task':
      return r.taskCode ? `/tarea/${seg(r.taskCode)}` : '/tarea';
    case 'legal':
      return `/${r.legalDoc ?? 'terminos'}`;
    case 'user':
      return r.userId ? `/u/${seg(r.userId)}` : '/jugadores';
    case 'study':
      return r.quizId ? `/estudiar/${seg(r.quizId)}` : '/';
    case 'create':
      return r.quizId ? `/editar/${seg(r.quizId)}` : '/crear';
    case 'mine':
      return r.mineTab === 'exams' ? '/mis-examenes' : r.mineTab === 'tasks' ? '/mis-tareas' : '/mis-trivias';
    case 'room':
      return r.pin ? `/sala/${seg(r.pin)}` : '/';
    case 'leaderboard':
      return r.leagueId ? `/ranking/liga/${seg(r.leagueId)}` : '/ranking';
    default:
      return FIXED[r.screen] ?? '/';
  }
}

export function pathToRoute(pathname: string = location.pathname): Route {
  const path = pathname.replace(/\/+$/, '') || '/';
  const [, first = '', second, third] = path.split('/');
  const arg = (s: string | undefined) => (s ? decodeURIComponent(s) : undefined);

  switch (first) {
    case '':
      return { screen: 'home', homeTab: 'quizzes' };
    case 'sala-de-espera':
      return { screen: 'home', homeTab: 'rooms' };
    case 't':
      if (second) return { screen: 'quiz', quizId: arg(second) };
      break;
    case 'e':
      if (second && /^\d{6}$/.test(second)) return { screen: 'exam', examCode: second };
      break;
    case 'terminos':
    case 'privacidad':
      return { screen: 'legal', legalDoc: first as LegalDoc };
    case 'u':
      if (second) return { screen: 'user', userId: arg(second) };
      break;
    case 'estudiar':
      if (second) return { screen: 'study', quizId: arg(second) };
      break;
    case 'crear':
      return { screen: 'create' };
    case 'editar':
      if (second) return { screen: 'create', quizId: arg(second) };
      break;
    case 'mis-trivias':
      return { screen: 'mine', mineTab: 'quizzes' };
    case 'mis-examenes':
      return { screen: 'mine', mineTab: 'exams' };
    case 'mis-tareas':
      return { screen: 'mine', mineTab: 'tasks' };
    case 'tarea':
      return { screen: 'task', taskCode: second && /^[A-Za-z0-9_-]{16}$/.test(second) ? second : undefined };
    case 'sala':
      if (second && /^\d{6}$/.test(second)) return { screen: 'room', pin: second };
      break;
    case 'ranking':
      if (second === 'liga' && third) return { screen: 'leaderboard', leagueId: arg(third) };
      return { screen: 'leaderboard' };
    default: {
      const fixed = (Object.keys(FIXED) as Screen[]).find((s) => FIXED[s] === `/${first}`);
      if (fixed) return { screen: fixed };
    }
  }
  return { screen: 'home', homeTab: 'quizzes' };
}
